import React, { useEffect } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  ADD_INSTRUCTION_LABEL,
  CANCEL_LABEL,
  CONFIRM_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
  EDIT_INSTRUCTION_LABEL,
  EDUCATIONAL_INSTITUTION_LABEL,
  EDUCATIONAL_QUALIFICATION_LABEL,
  ENDING_DATE_LABEL,
  INVALID_DATE_LABEL,
  IS_NOT_VALID_LABEL,
  IS_REQUIRED_LABEL,
  NAME_EDUCATIONAL_QUALIFICATION_LABEL,
  OTHER_LABEL,
  STARTING_DATE_LABEL,
  STUDY_PATH_LABEL,
  YEAR_END_LABEL,
  YEAR_START_LABEL,
} from "../../../../../../../constants/Dictionary";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import TextInput from "../../../../../../../components/core/TextInput/TextInput";
import { EDUCATIONS_OPTIONS } from "../../../../../../../constants/AppConstants";
import * as yup from "yup";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { StyledHistoryCourseModal } from "../../Courses/HistoryCourseModal/HistoryCourseModal.styled";
import {
  AddUpdateCollaboratorsEducation,
  CollaboratorDetails,
  CollaboratorHistoryEducation,
} from "../../../../../../../types/api/collaborators.type";
import {
  CalendarInput,
  CalendarMode,
} from "../../../../../../../components/core/CalendarInput/CalendarInput";
import dayjs from "dayjs";
import {
  EN_DATE_FORMAT,
  isMonthSameOrAfter,
} from "../../../../../../../utils/date/date.utils";
import {
  useEducationHistoryAdd,
  useEducationHistoryUpdate,
} from "../../../../../../../hooks/formations.hook";
type FormData = {
  qualification: string | null;
  institution: string;
  studyPath: string;
  startDate: Date | null;
  endDate?: Date | null;
  qualificationName?: string;
};

type HistoryInstructionModalProps = {
  education: CollaboratorHistoryEducation | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  collaborator: CollaboratorDetails;
};

export const HistoryEducationModal = ({
  education,
  isOpen,
  onClose,
  onSave,
  collaborator,
}: HistoryInstructionModalProps) => {
  const educationOptions: SelectOption<string>[] = EDUCATIONS_OPTIONS;

  const profileId = collaborator.id;

  const { mutateAsync: updateEducation, isPending: pendingUpdateEducation } =
    useEducationHistoryUpdate(profileId);
  const { mutateAsync: addEducation, isPending: pendingAddEducation } =
    useEducationHistoryAdd(profileId);

  const schema = yup.object({
    qualification: yup
      .string()
      .nullable()
      .required(`${EDUCATIONAL_QUALIFICATION_LABEL} ${IS_REQUIRED_LABEL}`),
    qualificationName: yup.string().when(["qualification"], {
      is: (qualification: string) => qualification === OTHER_LABEL,
      then: (field) =>
        field.required(
          `${NAME_EDUCATIONAL_QUALIFICATION_LABEL} ${IS_REQUIRED_LABEL}`,
        ),
      otherwise: (field) => field.notRequired(),
    }),
    institution: yup
      .string()
      .required(`${EDUCATIONAL_INSTITUTION_LABEL} ${IS_REQUIRED_LABEL}`),
    studyPath: yup
      .string()
      .required(`${STUDY_PATH_LABEL} ${IS_REQUIRED_LABEL}`),
    startDate: yup
      .date()
      .nullable()
      .typeError(`${INVALID_DATE_LABEL}`)
      .max(new Date(), `${STARTING_DATE_LABEL} ${IS_NOT_VALID_LABEL}`)
      .required(`${STARTING_DATE_LABEL} ${IS_REQUIRED_LABEL}`),
    endDate: yup
      .date()
      .nullable()
      .typeError(`${INVALID_DATE_LABEL}`)
      .max(new Date(), `${ENDING_DATE_LABEL} ${IS_NOT_VALID_LABEL}`)
      .test(
        "is-after-startDate",
        `La ${ENDING_DATE_LABEL} ${MUST_BE_AFTER_THAN_LABEL} ${STARTING_DATE_LABEL}.`,
        function (endDate) {
          const { startDate } = this.parent;
          if (!endDate || !startDate) {
            return true;
          }
          return isMonthSameOrAfter(endDate, startDate);
        },
      ),
  });

  const {
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  const watchQualification = watch("qualification");

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const qualificationName =
      data.qualification === OTHER_LABEL
        ? data.qualificationName!
        : data.qualification;

    const body: AddUpdateCollaboratorsEducation = {
      qualification: qualificationName ?? "",
      institution: data.institution,
      studyPath: data.studyPath,
      startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
      endDate: data.endDate ? dayjs(data.endDate).format(EN_DATE_FORMAT) : null,
    };

    if (education) {
      if (isDirty) {
        await updateEducation({ educationId: education.id, body });
      }
    } else {
      await addEducation(body);
    }
    closeModal();
    onSave();
  };

  const closeModal = () => {
    onClose();
  };

  const isQualificationInOptions = (value: string) =>
    EDUCATIONS_OPTIONS.some((opt) => opt.value === value);

  useEffect(() => {
    if (education) {
      const isInOptions = isQualificationInOptions(education.qualification);
      reset({
        qualification: isInOptions ? education.qualification : OTHER_LABEL,
        qualificationName: isInOptions ? "" : education.qualification,
        startDate: dayjs(education.startDate).toDate(),
        endDate: education.endDate ? dayjs(education.endDate).toDate() : null,
        institution: education.institution,
        studyPath: education.studyPath,
      });
    } else {
      reset({
        qualification: null,
        qualificationName: "",
        institution: "",
        studyPath: "",
        startDate: null,
        endDate: null,
      });
    }
  }, [education, isOpen]);

  const startDate = watch("startDate");

  return (
    <Modal
      fullSize={false}
      label={education ? EDIT_INSTRUCTION_LABEL : ADD_INSTRUCTION_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={pendingUpdateEducation || pendingAddEducation}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={pendingUpdateEducation || pendingAddEducation || !isDirty}
          type="submit"
          form="historyCourseForm"
        />
      }
    >
      <StyledHistoryCourseModal>
        <Loader loading={pendingUpdateEducation || pendingAddEducation}>
          <form id="historyCourseForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              <FormField error={errors.qualification?.message}>
                <Controller
                  name="qualification"
                  control={control}
                  render={({ field }) => (
                    <Select
                      variant={SelectVariantEnum.DEFAULT}
                      label={EDUCATIONAL_QUALIFICATION_LABEL}
                      options={educationOptions}
                      {...field}
                    />
                  )}
                />
              </FormField>
              {watchQualification === OTHER_LABEL && (
                <FormField error={errors.qualificationName?.message}>
                  <Controller
                    name="qualificationName"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={NAME_EDUCATIONAL_QUALIFICATION_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>
            <div className="input-row">
              <FormField error={errors.studyPath?.message}>
                <Controller
                  name="studyPath"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label={STUDY_PATH_LABEL}
                      maxLength={255}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.institution?.message}>
                <Controller
                  name="institution"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label={EDUCATIONAL_INSTITUTION_LABEL}
                      maxLength={255}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="input-row">
              <FormField error={errors.startDate?.message}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={YEAR_START_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      maxDate={new Date()}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.endDate?.message}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={YEAR_END_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      maxDate={new Date()}
                      minDate={
                        startDate && !isNaN(startDate.getTime())
                          ? startDate
                          : undefined
                      }
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
          </form>
        </Loader>
      </StyledHistoryCourseModal>
    </Modal>
  );
};
